/**
 * Shared handling for requests rejected because the session or its CSRF token is no longer valid
 * (419 Page Expired, 401 Unauthenticated).
 *
 * The rejected request is never replayed: the user's page and unsaved input stay as they are, and a
 * notice explains what to do. A single GET /session/status (deduplicated across concurrent failures)
 * reports whether the browser is still signed in; its response also refreshes the XSRF-TOKEN cookie
 * for whatever session the browser now holds, so the user's next attempt carries a valid token.
 */
import { reactive } from 'vue';

export const SESSION_STATUS_URL = '/session/status';

/**
 * kind: null | 'retry' (still signed in, token refreshed) | 'signed_out' | 'unknown' (status check failed)
 * path: the page the failure happened on; the notice stays up there until a write is accepted.
 */
export const sessionNotice = reactive({ kind: null, status: null, path: null });

let inFlight = null;

export function isSessionFailureStatus(status) {
    return status === 419 || status === 401;
}

async function fetchSessionStatus(fetchImpl) {
    const response = await fetchImpl(SESSION_STATUS_URL, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
        throw new Error(`Session status ${response.status}`);
    }
    const body = await response.json();
    return body?.authenticated === true;
}

/**
 * @param {number} status HTTP status of the rejected request
 * @param {{ fetchImpl?: typeof fetch, expectsAuthenticatedUser?: boolean }} [options]
 *        expectsAuthenticatedUser: false on guest pages (login, coverage), where "not signed in" is normal
 * @returns {Promise<string>} the notice kind that was shown
 */
export function reportSessionFailure(status, options = {}) {
    const { fetchImpl = globalThis.fetch, expectsAuthenticatedUser = currentPageHasUser() } = options;
    if (inFlight) {
        return inFlight;
    }
    inFlight = (async () => {
        let kind;
        try {
            const authenticated = await fetchSessionStatus(fetchImpl);
            kind = authenticated || !expectsAuthenticatedUser ? 'retry' : 'signed_out';
        } catch {
            kind = 'unknown';
        }
        sessionNotice.kind = kind;
        sessionNotice.status = status;
        sessionNotice.path = currentPath();
        return kind;
    })().finally(() => {
        inFlight = null;
    });
    return inFlight;
}

export function dismissSessionNotice() {
    sessionNotice.kind = null;
    sessionNotice.status = null;
    sessionNotice.path = null;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * A write that got through CSRF and authentication shows the session works again. Reads (partial
 * reloads, polling, status checks) prove nothing about the rejected write and leave the notice up.
 */
export function noteRequestAccepted(method, status) {
    if (!sessionNotice.kind || SAFE_METHODS.has(String(method || 'GET').toUpperCase())) {
        return;
    }
    if (status >= 200 && status < 400) {
        dismissSessionNotice();
    }
}

/** Leaving the page the failure happened on retires its notice; reloading the same page does not. */
export function notePageVisited(path) {
    if (sessionNotice.kind && sessionNotice.path && path && path !== sessionNotice.path) {
        dismissSessionNotice();
    }
}

function currentPath() {
    return globalThis.location?.pathname ?? null;
}

let pageHasUser = () => true;

/** app.js wires this to the current Inertia page's auth.user. */
export function setPageUserResolver(fn) {
    pageHasUser = fn;
}

function currentPageHasUser() {
    try {
        return Boolean(pageHasUser());
    } catch {
        return true;
    }
}

/** Test hook. */
export function resetSessionRecoveryForTests() {
    inFlight = null;
    pageHasUser = () => true;
    dismissSessionNotice();
}
